import { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import {
  ArrowLeft, Buildings, MapPin, ClockCounterClockwise, Globe, Phone,
  Sparkle, Image as ImageIcon, CheckCircle, Info, X, UploadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  "Preschool / Kindergarten", "Primary School", "Elementary School", "Middle School", "High School",
  "Secondary School", "Senior Secondary School", "Vocational School", "Technical School", "Training Institute",
  "Community College", "College", "University", "Graduate School", "Professional School", "Medical School",
  "Law School", "Business School", "Art / Design School", "Music School", "Teacher Training Institution",
  "Research Institution", "Other",
];

const EMPTY = {
  name: "", other_names: "", type: "University", country: "", state: "", city: "", district: "",
  locality: "", postal_code: "", address: "", founded: "", operating_status: "", closure_status: "",
  former_name: "", new_name: "", history: "", website: "", facebook: "", instagram: "", linkedin: "",
  wikipedia: "", other_links: "", institution_email: "", phone: "", head: "", extra: "", local_name: "",
  motto: "", nickname: "", source_relationship: "", submitter_name: "", submitter_email: "", submitter_phone: "",
  notify_me: true, credit_opt_in: false,
};

const Section = ({ icon: Icon, title, hint, children }) => (
  <div className="rounded-2xl border border-border/60 bg-card p-5">
    <div className="flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-secondary" />
      <h2 className="font-heading text-lg font-medium">{title}</h2>
    </div>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    <div className="mt-4 space-y-3">{children}</div>
  </div>
);

export default function SuggestInstitution() {
  useSeo("Suggest an Institution", "Help us make AlumniPulse™ truly global — tell us about an institution that isn't listed yet.");
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, country: sp.get("country") || "", state: sp.get("state") || "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [permission, setPermission] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { id: data.id, name: file.name }]);
      }
    } catch { toast.error("Upload failed — please try a smaller image"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!f.name.trim() || !f.country.trim()) { toast.error("Institution name and country are required"); return; }
    if (!user && !f.submitter_email.trim()) { toast.error("Please add your email so we can update you"); return; }
    setBusy(true);
    try {
      const payload = { ...f, photo_file_ids: photos.map((p) => p.id) };
      const { data } = await api.post("/public/institution-suggestions", payload);
      setDone({ dupes: data.possible_duplicates || [] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16 text-center" data-testid="suggest-institution-thankyou">
        <CheckCircle size={56} weight="duotone" className="mx-auto text-secondary" />
        <h1 className="mt-4 font-heading text-3xl font-light tracking-tight">Thank you!</h1>
        <p className="mt-3 text-muted-foreground">
          We've received your institution suggestion. Our team will research the information, verify available
          sources, and work to create the institution's AlumniPulse™ profile. We'll notify you when there's an update.
        </p>
        {done.dupes.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border/60 bg-muted/30 p-4 text-left" data-testid="suggest-dupes">
            <p className="text-sm font-medium">We may already have a match:</p>
            <div className="mt-2 space-y-1.5">
              {done.dupes.map((d) => (
                <Link key={d.id} to={`/institutions/${d.id}`} className="block text-sm text-secondary hover:underline">
                  {d.name} {(d.location || d.country) ? `· ${[d.location, d.country].filter(Boolean).join(", ")}` : ""}
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button variant="outline" className="rounded-full" onClick={() => { setF(EMPTY); setPhotos([]); setDone(null); }} data-testid="suggest-another-btn">Suggest another institution</Button>
          <Link to="/explore/institutions"><Button className="rounded-full" data-testid="explore-institutions-btn">Explore institutions</Button></Link>
          {user && <Link to="/my-suggestions"><Button variant="ghost" className="rounded-full">Track my suggestions</Button></Link>}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-12" data-testid="suggest-institution-page">
      <Link to="/explore/institutions" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Institutions</Link>
      <div className="mt-4 flex items-center gap-2">
        <Buildings size={26} weight="duotone" className="text-secondary" />
        <h1 className="font-heading text-3xl font-light tracking-tight">Suggest an Institution</h1>
      </div>
      <p className="mt-2 text-muted-foreground">Help us make AlumniPulse™ truly global. Tell us about an institution that isn't listed yet.</p>
      <div className="mt-3 flex items-start gap-2 rounded-xl bg-secondary/5 p-3 text-sm text-muted-foreground">
        <Info size={16} className="mt-0.5 shrink-0 text-secondary" />
        <span><strong className="text-foreground">Don't know everything? That's okay.</strong> A name, location, old photograph, website, or even a few memories may be enough to help us find it.</span>
      </div>

      <div className="mt-6 space-y-5">
        <Section icon={Buildings} title="Institution information">
          <div><Label>Institution name <span className="text-secondary">*</span></Label><Input className="mt-1" value={f.name} onChange={set("name")} placeholder="Full official name" data-testid="si-name" /></div>
          <div><Label>Known / former / local names</Label><Input className="mt-1" value={f.other_names} onChange={set("other_names")} placeholder="Alternative, former, local names or abbreviation" data-testid="si-other-names" /></div>
          <div>
            <Label>Institution type <span className="text-secondary">*</span></Label>
            <Select value={f.type} onValueChange={(v) => setF((s) => ({ ...s, type: v }))}>
              <SelectTrigger className="mt-1" data-testid="si-type"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </Section>

        <Section icon={MapPin} title="Where is it located?" hint="Exact coordinates aren't required — tell us what you know.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Country <span className="text-secondary">*</span></Label><Input className="mt-1" value={f.country} onChange={set("country")} placeholder="e.g. India" data-testid="si-country" /></div>
            <div><Label>State / Province / Region</Label><Input className="mt-1" value={f.state} onChange={set("state")} placeholder="e.g. Kerala" /></div>
            <div><Label>City / Town</Label><Input className="mt-1" value={f.city} onChange={set("city")} placeholder="e.g. Kochi" data-testid="si-city" /></div>
            <div><Label>District / County</Label><Input className="mt-1" value={f.district} onChange={set("district")} /></div>
            <div><Label>Neighborhood / Locality</Label><Input className="mt-1" value={f.locality} onChange={set("locality")} /></div>
            <div><Label>Postal / ZIP code</Label><Input className="mt-1" value={f.postal_code} onChange={set("postal_code")} /></div>
          </div>
          <div><Label>Known address</Label><Input className="mt-1" value={f.address} onChange={set("address")} placeholder="Street / area if you know it" /></div>
        </Section>

        <Section icon={ClockCounterClockwise} title="What do you know about it?">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Founded / established year</Label><Input className="mt-1" value={f.founded} onChange={set("founded")} placeholder="Approx. year" /></div>
            <div>
              <Label>Currently operating?</Label>
              <Select value={f.operating_status || "unset"} onValueChange={(v) => setF((s) => ({ ...s, operating_status: v === "unset" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="si-operating"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not specified</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="not_sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.operating_status === "no" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Status</Label>
                <Select value={f.closure_status || "unset"} onValueChange={(v) => setF((s) => ({ ...s, closure_status: v === "unset" ? "" : v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">Not specified</SelectItem>
                    {["Closed", "Merged", "Renamed", "Relocated", "Converted", "Unknown"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>New / current name (if any)</Label><Input className="mt-1" value={f.new_name} onChange={set("new_name")} /></div>
            </div>
          )}
          <div><Label>Brief history</Label><Textarea className="mt-1 min-h-[80px]" value={f.history} onChange={set("history")} placeholder="Anything you know about its history, founders, campus, students, teachers, or community." /></div>
        </Section>

        <Section icon={Globe} title="Online information" hint="Optional — many small or older institutions have little online presence.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Official website</Label><Input className="mt-1" value={f.website} onChange={set("website")} placeholder="https://" data-testid="si-website" /></div>
            <div><Label>Facebook</Label><Input className="mt-1" value={f.facebook} onChange={set("facebook")} placeholder="https://" /></div>
            <div><Label>Instagram</Label><Input className="mt-1" value={f.instagram} onChange={set("instagram")} placeholder="https://" /></div>
            <div><Label>LinkedIn</Label><Input className="mt-1" value={f.linkedin} onChange={set("linkedin")} placeholder="https://" /></div>
            <div><Label>Wikipedia</Label><Input className="mt-1" value={f.wikipedia} onChange={set("wikipedia")} placeholder="https://" /></div>
            <div><Label>Other source</Label><Input className="mt-1" value={f.other_links} onChange={set("other_links")} placeholder="Government listing, article…" /></div>
          </div>
        </Section>

        <Section icon={Phone} title="Institution contact (optional)" hint="Only publicly available institutional information — no personal contacts.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Official email</Label><Input className="mt-1" value={f.institution_email} onChange={set("institution_email")} placeholder="We'll let them know about AlumniPulse" data-testid="si-inst-email" /></div>
            <div><Label>Public phone</Label><Input className="mt-1" value={f.phone} onChange={set("phone")} /></div>
          </div>
          <div><Label>Principal / President / Head</Label><Input className="mt-1" value={f.head} onChange={set("head")} /></div>
        </Section>

        <Section icon={Sparkle} title="Help us identify it">
          <div><Label>What makes this institution unique?</Label><Textarea className="mt-1 min-h-[70px]" value={f.extra} onChange={set("extra")} placeholder="Known teachers, departments, courses, campuses, colors, alumni, graduation years…" /></div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><Label>Local-language name</Label><Input className="mt-1" value={f.local_name} onChange={set("local_name")} /></div>
            <div><Label>Motto</Label><Input className="mt-1" value={f.motto} onChange={set("motto")} /></div>
            <div><Label>Nickname</Label><Input className="mt-1" value={f.nickname} onChange={set("nickname")} /></div>
          </div>
        </Section>

        <Section icon={ImageIcon} title="Have a photo or document?" hint={user ? "Building, signboard, brochure, yearbook, logo, old photograph…" : "Sign in to attach photos or documents."}>
          {user ? (
            <>
              <input ref={fileRef} type="file" accept="image/*,.pdf" multiple hidden onChange={upload} data-testid="si-file-input" />
              <Button variant="outline" className="rounded-full gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="si-upload-btn">
                <UploadSimple size={16} /> {uploading ? "Uploading…" : "Upload photos / documents"}
              </Button>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs">
                      {p.name.slice(0, 24)}
                      <button onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
              {photos.length > 0 && (
                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={permission} onCheckedChange={(v) => setPermission(!!v)} data-testid="si-permission" className="mt-0.5" />
                  I have the right or appropriate permission to submit these materials.
                </label>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground"><Link to="/login" className="text-secondary hover:underline">Sign in</Link> to attach photos — or submit without them, that's fine too.</p>
          )}
        </Section>

        <Section icon={Info} title="A few last details">
          <div>
            <Label>How do you know about this institution?</Label>
            <Select value={f.source_relationship || "unset"} onValueChange={(v) => setF((s) => ({ ...s, source_relationship: v === "unset" ? "" : v }))}>
              <SelectTrigger className="mt-1" data-testid="si-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Prefer not to say</SelectItem>
                {["I attended this institution", "I currently attend this institution", "I currently work there", "I previously worked there", "I am an alumnus/alumna", "A family member attended", "I live nearby", "I am researching it", "I found it online", "I represent the institution", "Other"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {user ? (
            <div className="rounded-xl bg-muted/30 p-3 text-sm">
              You're submitting this suggestion as <strong>{user.name}</strong>.
              <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={f.notify_me} onCheckedChange={(v) => setF((s) => ({ ...s, notify_me: !!v }))} data-testid="si-notify" />
                Notify me when it's added
              </label>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Your name</Label><Input className="mt-1" value={f.submitter_name} onChange={set("submitter_name")} data-testid="si-submitter-name" /></div>
              <div><Label>Your email <span className="text-secondary">*</span></Label><Input className="mt-1" value={f.submitter_email} onChange={set("submitter_email")} placeholder="So we can update you" data-testid="si-submitter-email" /></div>
              <div className="sm:col-span-2"><Label>Phone (optional)</Label><Input className="mt-1" value={f.submitter_phone} onChange={set("submitter_phone")} /></div>
            </div>
          )}

          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <Checkbox checked={f.credit_opt_in} onCheckedChange={(v) => setF((s) => ({ ...s, credit_opt_in: !!v }))} data-testid="si-credit" className="mt-0.5" />
            You may credit me publicly as having helped add this institution ("Added with help from {user ? user.name : "your name"}").
          </label>
        </Section>

        <Button className="w-full rounded-full" size="lg" onClick={submit} disabled={busy} data-testid="si-submit">
          {busy ? "Submitting…" : "Submit suggestion"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">Simple for you. Thorough behind the scenes. We'll take it from here.</p>
      </div>
    </div>
  );
}
